// Field-level machinery shared by the per-collection models: how one JSON value is read (leniently, like
// GroundTeam/Sync/ResilientDecoding.swift) and how a value is written into a brand-new payload (strictly, like Swift's
// encoder would). Only contracts/ uses this; screens and sync code use the per-collection decodeX/newX functions.
//
// Reading follows what an iPhone does with a pulled payload (SyncReplica.merge → ResilientDecoding.decode):
// - a missing key takes the value of the type's `decodingTemplate` (the `missing` thunk of each field);
// - an unknown key is ignored by Swift and kept here untouched (passthrough), so nothing a newer app wrote is lost;
// - a wrong JSON type, a `null` for a non-optional, a bad UUID or date, or an unknown enum raw value makes Swift's
//   decode throw, i.e. the item is undecodable on iOS. We record why (`undecodable`) and still return a best-effort
//   value, so the console can show the item read-only without ever editing it.

import type { JsonObject } from '../wire';
import { isUuid, sameId } from '../uuid';
import { toWireDate } from '../wireDate';

/** A decoded payload plus, when iOS could not read it, why (such items are shown read-only, never edited). */
export interface Decoded<T> {
  value: T;
  undecodable?: string;
}

/** Marks a value `read` could not accept; the caller substitutes the field default. */
export const INVALID: unique symbol = Symbol('invalid');
export type Invalid = typeof INVALID;

/** How one JSON value maps to a Swift property type. */
export interface Codec<T> {
  /** Swift type name used in problem messages ("String", "Int", "RecordType", ...). */
  readonly label: string;
  /** Reads a present, non-null JSON value. Problems iOS would reject go into `issues`. */
  read(raw: unknown, path: string, issues: string[]): T | Invalid;
  /** Checks and normalizes a value for a new payload (ids upper-cased, Ints rounded); throws RangeError. */
  write(value: T, path: string): T;
  /** Stand-in when there is no usable value and no template default (best effort only). */
  fallback(): T;
}

/**
 * One stored property. `optional` = a Swift Optional (omitted when nil, never `null` on the wire). `missing` = the
 * decodingTemplate value used when the key is absent; a non-optional field without it (an `id`, or a property of an
 * Optional nested struct such as `coordinate.latitude`) makes a missing key a decode failure, as in Swift.
 */
export interface FieldSpec<T, O extends boolean = boolean> {
  readonly codec: Codec<T>;
  readonly optional: O;
  readonly missing?: () => T;
}

/** Declared keys of a payload type (drops the `[unknown: string]: unknown` passthrough signature). */
export type Known<T> = { [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K] };

/** A spec for every declared key of `T`, optional exactly where `T`'s key is optional. */
export type FieldMap<T> = {
  [K in keyof T as string extends K ? never : number extends K ? never : K]-?: FieldSpec<
    Exclude<T[K], undefined>,
    {} extends Pick<T, K> ? true : false
  >;
};

/** `[unknown: string]: unknown`: keys a newer app version wrote survive every read. */
export interface Passthrough {
  [unknown: string]: unknown;
}

// MARK: - Field constructors

export const field = {
  /** Non-optional with a template default. */
  req<T>(codec: Codec<T>, missing: () => T): FieldSpec<T, false> {
    return { codec, optional: false, missing };
  },
  /** Swift Optional: missing or null reads as nil; nil is written by omitting the key. */
  opt<T>(codec: Codec<T>): FieldSpec<T, true> {
    return { codec, optional: true };
  },
  /** Non-optional whose absence Swift cannot fill (no template value for it). */
  strict<T>(codec: Codec<T>): FieldSpec<T, false> {
    return { codec, optional: false };
  },
};

// MARK: - Scalars

function describe(raw: unknown): string {
  if (raw === null) return 'null';
  if (Array.isArray(raw)) return 'an array';
  if (typeof raw === 'string') return `"${raw.length > 40 ? `${raw.slice(0, 40)}…` : raw}"`;
  if (typeof raw === 'object') return 'an object';
  return String(raw);
}

function scalar<T>(label: string, accepts: (raw: unknown) => raw is T, fallback: () => T, write?: (v: T, path: string) => T): Codec<T> {
  return {
    label,
    read(raw, path, issues) {
      if (accepts(raw)) return raw;
      issues.push(`${path}: expected ${label}, got ${describe(raw)}`);
      return INVALID;
    },
    write(value, path) {
      if (!accepts(value)) throw new RangeError(`${path}: expected ${label}, got ${describe(value)}`);
      return write ? write(value, path) : value;
    },
    fallback,
  };
}

export const STRING: Codec<string> = scalar('String', (v): v is string => typeof v === 'string', () => '');

export const BOOL: Codec<boolean> = scalar('Bool', (v): v is boolean => typeof v === 'boolean', () => false);

/** Swift Double. JSON has no NaN/Infinity, and JSONSerialization refuses to write them, so neither may we. */
export const DOUBLE: Codec<number> = scalar(
  'Double',
  (v): v is number => typeof v === 'number' && Number.isFinite(v),
  () => 0,
);

/**
 * Swift Int. JSONDecoder rejects 2.5 for an Int, so a fraction is a decode failure; new payloads round (WEB_PLAN §0:
 * `Math.round` before writing `order`, `probability`, `workdayStartHour`, ...).
 */
export const INT: Codec<number> = {
  label: 'Int',
  read(raw, path, issues) {
    if (typeof raw === 'number' && Number.isSafeInteger(raw)) return raw;
    issues.push(`${path}: expected Int, got ${describe(raw)}`);
    return typeof raw === 'number' && Number.isFinite(raw) ? Math.round(raw) : INVALID;
  },
  write(value, path) {
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new RangeError(`${path}: expected Int, got ${describe(value)}`);
    const n = Math.round(value);
    if (!Number.isSafeInteger(n)) throw new RangeError(`${path}: Int out of range (${value})`);
    return n === 0 ? 0 : n; // never -0
  },
  fallback: () => 0,
};

/**
 * Swift UUID: any-case uuid string (`UUID(uuidString:)` accepts both). Read values are kept exactly as received (the
 * web never re-cases an untouched value); new payloads get Swift's upper-case `uuidString` form.
 */
export const UUID: Codec<string> = {
  label: 'UUID',
  read(raw, path, issues) {
    if (isUuid(raw)) return raw;
    issues.push(`${path}: expected UUID, got ${describe(raw)}`);
    return INVALID;
  },
  write(value, path) {
    if (!isUuid(value)) throw new RangeError(`${path}: expected UUID, got ${describe(value)}`);
    return value.toUpperCase();
  },
  fallback: () => '00000000-0000-0000-0000-000000000000',
};

/**
 * Dates iOS can decode: JSONDecoder `.iso8601` = ISO8601DateFormatter `.withInternetDateTime`, which takes whole
 * seconds with `Z` or a `±hh:mm` offset and rejects fractional seconds (the iOS 17 decoder, WEB_PLAN §0).
 */
const IOS_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:Z|[+-](\d{2}):(\d{2}))$/;

/**
 * The fields must name a real instant. `Date.parse` alone is not enough: V8 rolls `2026-02-30` over to March 2 and
 * takes `24:00:00`, while Apple's formatter (non-lenient) refuses both, so such a string would pass here, be written by
 * the web, and make the item undecodable on every iPhone.
 */
function isRealDate(m: RegExpExecArray): boolean {
  const [y, mo, d, h, mi, s] = m.slice(1, 7).map(Number) as [number, number, number, number, number, number];
  if (h > 23 || mi > 59 || s > 59) return false;
  if (m[7] !== undefined && (Number(m[7]) > 23 || Number(m[8]) > 59)) return false;
  const day = new Date(0);
  day.setUTCFullYear(y, mo - 1, d); // not Date.UTC, which maps years 0-99 to 19xx
  return day.getUTCFullYear() === y && day.getUTCMonth() === mo - 1 && day.getUTCDate() === d;
}

/** True for a date string an iPhone's decoder accepts. */
export function isIosDecodableDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = IOS_DATE_RE.exec(value);
  return m !== null && isRealDate(m) && Number.isFinite(Date.parse(value));
}

/** True for a date string in the exact form the web writes (`2026-09-29T14:03:11Z`). */
export function isWireDate(value: unknown): value is string {
  return isIosDecodableDate(value) && value.endsWith('Z');
}

/** Swift Date as a payload string (kept as received when read; strict `toWireDate` form when written). */
export const DATE: Codec<string> = {
  label: 'Date',
  read(raw, path, issues) {
    if (isIosDecodableDate(raw)) return raw;
    const why = typeof raw === 'string' && /\.\d+(?:Z|[+-]\d{2}:?\d{2})$/.test(raw) ? ' (fractional seconds)' : '';
    issues.push(`${path}: expected an ISO 8601 date${why}, got ${describe(raw)}`);
    return INVALID;
  },
  write(value, path) {
    if (!isWireDate(value)) {
      throw new RangeError(`${path}: dates must be whole-second UTC like 2026-09-29T14:03:11Z (use toWireDate), got ${describe(value)}`);
    }
    return value;
  },
  fallback: () => EPOCH,
};

/** `Date(timeIntervalSince1970: 0)` as the web writes it. */
export const EPOCH = '1970-01-01T00:00:00Z';

/** `.now` as a payload date (whole seconds). */
export function nowWire(): string {
  return toWireDate(new Date());
}

/** A raw-value enum. Unknown values are undecodable on iOS; best effort falls back to the field default. */
export function enumOf<T extends string>(label: string, list: readonly T[]): Codec<T> {
  const ok = (v: unknown): v is T => typeof v === 'string' && (list as readonly string[]).includes(v);
  return {
    label,
    read(raw, path, issues) {
      if (ok(raw)) return raw;
      issues.push(`${path}: unknown ${label} ${describe(raw)}`);
      return INVALID;
    },
    write(value, path) {
      if (!ok(value)) throw new RangeError(`${path}: unknown ${label} ${describe(value)}`);
      return value;
    },
    fallback: () => list[0] as T,
  };
}

// MARK: - Containers

/**
 * Swift Array. An element Swift could not decode fails the whole item; best effort drops just that element so the
 * rest can still be shown.
 */
export function arrayOf<T>(element: Codec<T>): Codec<T[]> {
  return {
    label: `[${element.label}]`,
    read(raw, path, issues) {
      if (!Array.isArray(raw)) {
        issues.push(`${path}: expected [${element.label}], got ${describe(raw)}`);
        return INVALID;
      }
      const out: T[] = [];
      raw.forEach((item, i) => {
        const p = `${path}[${i}]`;
        if (item === null || item === undefined) {
          issues.push(`${p}: null in [${element.label}]`);
          return;
        }
        const v = element.read(item, p, issues);
        if (v !== INVALID) out.push(v);
      });
      return out;
    },
    write(values, path) {
      if (!Array.isArray(values)) throw new RangeError(`${path}: expected [${element.label}], got ${describe(values)}`);
      return values.map((v, i) => {
        if (v === null || v === undefined) throw new RangeError(`${path}[${i}]: null in [${element.label}]`);
        return element.write(v, `${path}[${i}]`);
      });
    },
    fallback: () => [],
  };
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Own-property assignment that is safe for any key a payload may carry (including `__proto__`). */
function setOwn(target: Record<string, unknown>, key: string, value: unknown): void {
  Object.defineProperty(target, key, { value, enumerable: true, writable: true, configurable: true });
}

const hasOwn = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

/**
 * Reads the declared keys of `raw` (template defaults for missing ones) and copies every other key untouched, except
 * `skip` (local-only keys, which iOS strips before decoding and never validates).
 */
export function readFields(
  fields: Readonly<Record<string, FieldSpec<unknown>>>,
  raw: Record<string, unknown>,
  path: string,
  issues: string[],
  skip: readonly string[] = [],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, spec] of Object.entries(fields)) {
    const p = path ? `${path}.${key}` : key;
    const v = hasOwn(raw, key) ? raw[key] : undefined;
    if (v === undefined || v === null) {
      if (spec.optional) continue; // nil: Swift decodeIfPresent accepts both a missing key and null
      if (v === null) issues.push(`${p}: null for non-optional ${spec.codec.label}`);
      if (spec.missing && v === undefined) {
        setOwn(out, key, spec.missing());
      } else {
        if (v === undefined) issues.push(`${p}: missing ${spec.codec.label}`);
        setOwn(out, key, spec.missing ? spec.missing() : spec.codec.fallback());
      }
      continue;
    }
    const r = spec.codec.read(v, p, issues);
    if (r !== INVALID) setOwn(out, key, r);
    else if (!spec.optional) setOwn(out, key, spec.missing ? spec.missing() : spec.codec.fallback());
  }
  for (const key of Object.keys(raw)) {
    if (!hasOwn(fields, key) && !skip.includes(key)) setOwn(out, key, raw[key]);
  }
  return out;
}

/**
 * Checks a value for a new payload: every non-optional key present, nil optionals omitted (never null), each value
 * normalized by its codec. Undeclared keys are copied as they are, except `drop` (local-only keys).
 */
export function writeFields(
  fields: Readonly<Record<string, FieldSpec<unknown>>>,
  value: Record<string, unknown>,
  path: string,
  drop: readonly string[] = [],
): Record<string, unknown> {
  if (!isPlainObject(value)) throw new RangeError(`${path || 'payload'}: expected an object, got ${describe(value)}`);
  const out: Record<string, unknown> = {};
  for (const [key, spec] of Object.entries(fields)) {
    const p = path ? `${path}.${key}` : key;
    const v = hasOwn(value, key) ? value[key] : undefined;
    if (v === undefined) {
      if (spec.optional) continue;
      throw new RangeError(`${p}: missing (every non-optional key is written)`);
    }
    if (v === null) throw new RangeError(`${p}: null is never written (omit the key for nil)`);
    setOwn(out, key, spec.codec.write(v, p));
  }
  for (const key of Object.keys(value)) {
    if (hasOwn(fields, key) || drop.includes(key)) continue;
    const v = value[key];
    if (v !== undefined) setOwn(out, key, v);
  }
  return out;
}

/** A nested struct (Address, Coordinate, Stage, RouteStop, ...), unknown keys kept. */
export function objectOf<T>(label: string, fields: FieldMap<T>): Codec<T> {
  const specs = fields as unknown as Readonly<Record<string, FieldSpec<unknown>>>;
  return {
    label,
    read(raw, path, issues) {
      if (!isPlainObject(raw)) {
        issues.push(`${path}: expected ${label}, got ${describe(raw)}`);
        return INVALID;
      }
      return readFields(specs, raw, path, issues) as T;
    },
    write(value, path) {
      return writeFields(specs, value as Record<string, unknown>, path) as T;
    },
    fallback: () => readFields(specs, {}, '', []) as T,
  };
}

// MARK: - Whole payloads

const MAX_REASONS = 4;

/** Joins decode problems into the `undecodable` reason shown in the UI and logs. */
export function reasonOf(issues: readonly string[]): string | undefined {
  if (issues.length === 0) return undefined;
  const head = issues.slice(0, MAX_REASONS).join('; ');
  return issues.length > MAX_REASONS ? `${head}; and ${issues.length - MAX_REASONS} more` : head;
}

export interface ModelOptions {
  /** Keys iOS strips before decoding (Record/User `localOnlyKeys`): ignored on read, dropped on write. */
  localOnly?: readonly string[];
  /** The payload carries `id` (every collection but settings). */
  hasId?: boolean;
}

/**
 * Decodes one payload. iOS also drops an item whose payload `id` differs from the envelope id
 * (SyncReplica.merge: `v.id == name.id`); pass the envelope id to apply the same rule.
 */
export function decodeModel<T>(
  fields: FieldMap<T>,
  raw: JsonObject,
  options: ModelOptions,
  envelopeId?: string,
): Decoded<T> {
  const issues: string[] = [];
  let source: Record<string, unknown> = raw;
  if (!isPlainObject(raw)) {
    issues.push(`payload: expected a JSON object, got ${describe(raw)}`);
    source = {};
  }
  const specs = fields as unknown as Readonly<Record<string, FieldSpec<unknown>>>;
  const value = readFields(specs, source, '', issues, options.localOnly);
  if (options.hasId !== false && envelopeId !== undefined && isUuid(value.id) && !sameId(value.id, envelopeId)) {
    issues.push(`id: ${String(value.id)} does not match the envelope id ${envelopeId}`);
  }
  const undecodable = reasonOf(issues);
  return undecodable === undefined ? { value: value as T } : { value: value as T, undecodable };
}

/** Builds a new payload: defaults, then the caller's values (undefined = nil), checked and normalized. */
export function createModel<T>(
  fields: FieldMap<T>,
  defaults: Record<string, unknown>,
  init: object,
  collection: string,
  options: ModelOptions = {},
): T {
  const merged: Record<string, unknown> = { ...defaults };
  for (const [key, v] of Object.entries(init)) {
    if (v !== undefined) setOwn(merged, key, v);
  }
  const specs = fields as unknown as Readonly<Record<string, FieldSpec<unknown>>>;
  return writeFields(specs, merged, collection, options.localOnly) as T;
}

/** Keys of a field map whose Swift property is Optional. */
export function optionalKeysOf<T>(fields: FieldMap<T>): string[] {
  return Object.entries(fields as unknown as Record<string, FieldSpec<unknown>>)
    .filter(([, spec]) => spec.optional)
    .map(([key]) => key);
}
